import {
	ApplicationRef,
	ComponentRef,
	createComponent,
	inject,
	Injectable,
	RendererFactory2,
	untracked
} from '@angular/core';
import { BaseNotificationItemConfig } from '../base-popover';
import { ToastConfig } from '../toast-popover.component';
import { ToastStackComponent } from '../toast-stack.component';

export type NotificationItemConfig = BaseNotificationItemConfig & ToastConfig;
export type NotificationStackOptions = Readonly<NotificationItemConfig & { _id: number; isLeaving?: boolean }>;

@Injectable({
	providedIn: 'root',
})
export class NotificationStackService {
	readonly #appRef = inject(ApplicationRef);
	readonly #renderer = inject(RendererFactory2).createRenderer(null, null);
	
	#idCounter = 0;
	readonly #toastStackRef: ComponentRef<ToastStackComponent>;

	constructor() {
		this.#toastStackRef = createComponent(ToastStackComponent, {
			environmentInjector: this.#appRef.injector,
		});
		this.#renderer.appendChild(document.body, this.#toastStackRef.location.nativeElement);
		this.#appRef.attachView(this.#toastStackRef.hostView);
	}

	public async open(options: NotificationItemConfig) {
		const popoverId = this.#idCounter++;
		const toastOptions = {
			...options,
			_id: popoverId,
		};

		const updateFn = () => {
			this.#toastStackRef.instance.toasts.update(toasts => [...toasts, toastOptions as any]);
			this.#toastStackRef.changeDetectorRef.detectChanges();
		};

		updateFn();

		const { data, onOpen } = options;
		onOpen?.(
			new CustomEvent('open', {
				detail: data,
			})
		);

		return {
			close: () => this.close(popoverId),
		};
	}

	public async closeAll() {
		const updateFn = () => {
			this.#toastStackRef.instance.toasts.set([]);
			this.#toastStackRef.changeDetectorRef.detectChanges();
		};

		updateFn();
	}

	public hasId(id: number) {
		return untracked(() => this.#toastStackRef.instance.toasts()).some((toast) => {
			const data = toast.data;
			return 'id' in data && data.id === id;
		});
	}

	public exists(predicate: (toast: NotificationStackOptions) => boolean) {
		return untracked(() => this.#toastStackRef.instance.toasts()).some(predicate);
	}

	public closeBy(predicate: (toast: NotificationStackOptions) => boolean) {
		untracked(() => this.#toastStackRef.instance.toasts())
			.filter(predicate)
			.forEach(toast => this.close(toast._id));
	}

	public async close(id: number) {
		const toasts = this.#toastStackRef.instance.toasts();
		const index = toasts.findIndex(t => t._id === id);
		if (index === -1) return;

		const toast = toasts[index];
		const { data, onClose } = toast;

		onClose?.(
			new CustomEvent('close', {
				detail: data,
			})
		);

		const updateFn = () => {
			this.#toastStackRef.instance.toasts.update(toasts => toasts.filter(t => t._id !== id));
			this.#toastStackRef.changeDetectorRef.detectChanges();
		};

		updateFn();
	}
}
